import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Route, BadgeCheck, Smartphone, Compass, Download, ArrowUpRight, Map } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, SectionHead } from "@/components/Motion";
import RouteMap from "@/components/RouteMap";
import CTA from "@/components/CTA";
import { IMAGES, CATEGORIES } from "@/lib/site";

const FEATURES = [
  {
    icon: Route,
    title: "Custom detours",
    body: "Craving a cortado? Detour mode re-routes you to the nearest approved stop and guides you right back to the path — no momentum lost.",
  },
  {
    icon: BadgeCheck,
    title: "Approved stops only",
    body: "No franchise noise, no stale pins, no mystery dots. Every stop on VeloPaths is vetted and approved — never scraped from a generic POI dump.",
  },
  {
    icon: Smartphone,
    title: "Clean, one-thumb interface",
    body: "Glanceable cards, big tap targets, and high-contrast pins. Designed to be read mid-pedal, in the sun, with one hand on the bars.",
  },
  {
    icon: Compass,
    title: "Powered by Google Maps SDK",
    body: "Rock-solid navigation underneath, our curation on top. You get the reliability you trust with a map that finally understands bikes.",
  },
];

const scrollToWaitlist = () => {
  if (window.__lenis) window.__lenis.scrollTo("#waitlist", { offset: -60 });
  else document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
};

export default function Riders() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#waitlist") {
      const t = setTimeout(scrollToWaitlist, 500);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <main data-testid="page-riders">
      <PageHero
        testid="riders-hero"
        image={IMAGES.heroRiders}
        overline="For riders"
        lines={["Turn Every Ride", "Into an Expedition."]}
        sub="VeloPaths pairs beautiful bike paths with the local stops that make them unforgettable — and filters out everything else."
      >
        <a
          href="https://play.google.com/store/apps/details?id=com.velopaths.rider&hl=en"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="btn-download-android"
          className="inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-6 py-3 text-sm font-bold text-white shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39]"
        >
          <Download size={15} /> Download for Android
        </a>
        <a
          href="https://apps.apple.com/us/app/velopaths-rider/id6817941154"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="btn-download-ios"
          aria-label="Download VeloPaths on the App Store"
          className="inline-flex items-center gap-2 rounded-full bg-[#F4F7F4] px-6 py-3 text-sm font-bold text-[#0B2B22] shadow-lg transition-transform duration-300 hover:-translate-y-0.5"
        >
          <Download size={15} /> Download for iOS
        </a>
        <a
          href={process.env.REACT_APP_EXPLORE_URL}
          data-testid="riders-explore-map"
          className="inline-flex items-center gap-2 rounded-full border border-[#F4F7F4]/40 px-6 py-3 text-sm font-bold text-[#F4F7F4] backdrop-blur transition-colors duration-300 hover:bg-[#F4F7F4]/10"
        >
          <Map size={15} /> Browse the Trail Map
        </a>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <SectionHead
          overline="The map, minus the noise"
          title="Every pin earns its place."
          sub="Try it: filter the map by what you're craving, tap a pin, and preview a detour. This is exactly how the app treats your ride — curated stops, zero clutter."
        />
        <Reveal delay={0.15} className="mt-12 text-[#0B2B22]">
          <RouteMap />
        </Reveal>
        <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <span
              key={c.id}
              data-testid={`category-chip-${c.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#154234]/15 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#0B2B22]"
            >
              <c.icon size={14} style={{ color: c.color }} />
              {c.label}
            </span>
          ))}
        </Reveal>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <SectionHead
            overline="Why riders love it"
            title="Made for the saddle, not the desk."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.1} className="h-full">
                <div className="group h-full rounded-[1.75rem] border border-[#154234]/10 bg-[#F4F7F4] p-7 transition-transform duration-500 hover:-translate-y-1.5">
                  <span className="grid size-11 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998] transition-colors duration-300 group-hover:bg-[#E05A47] group-hover:text-white">
                    <f.icon size={19} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                    {f.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#52635B]">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 -translate-x-5 translate-y-5 rounded-[2rem] bg-[#2D6A4F]/15" />
              <img
                src={IMAGES.cafe}
                alt="A café stop discovered on a VeloPaths detour"
                loading="lazy"
                decoding="async"
                className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHead
              overline="Detour mode"
              title="The best stops are three minutes off the path."
              sub="Generic maps bury the good stuff under thousands of pins. VeloPaths flips it: a short, signed detour takes you to a vetted local favorite — then drops you back on the route like nothing happened."
            />
            <Reveal delay={0.15} className="mt-8">
              <Link
                to="/about"
                data-testid="btn-riders-how-it-works"
                className="inline-flex items-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors duration-300 hover:bg-[#154234]"
              >
                How VeloPaths works <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="waitlist" className="relative overflow-hidden bg-[#0B2B22] py-24 text-[#F4F7F4] sm:py-28">
        <div className="grain absolute inset-0" />
        <div className="absolute -left-24 top-0 size-72 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 sm:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                Available now · iOS + Android
              </p>
              <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug">
                VeloPaths is here. Grab it on the App Store or Google Play.
              </h2>
              <p className="mt-4 max-w-xl text-base sm:text-lg text-[#F4F7F4]/70">
                The wait is over — download VeloPaths today and start exploring curated bike
                paths and the local stops along them.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-6">
                <div className="flex gap-4">
                  <figure className="rounded-2xl bg-[#F4F7F4] p-3 shadow-2xl">
                    <img
                      src="/images/app-store-qr.png"
                      alt="QR code linking to VeloPaths on the App Store"
                      data-testid="app-store-qr"
                      className="size-28 sm:size-32"
                    />
                    <figcaption className="mt-1.5 text-center font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-[#0B2B22]/70">
                      iOS
                    </figcaption>
                  </figure>
                  <figure className="rounded-2xl bg-[#F4F7F4] p-3 shadow-2xl">
                    <img
                      src="/images/play-store-qr.png"
                      alt="QR code linking to VeloPaths on the Google Play Store"
                      data-testid="play-store-qr"
                      className="size-28 sm:size-32"
                    />
                    <figcaption className="mt-1.5 text-center font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-[#0B2B22]/70">
                      Android
                    </figcaption>
                  </figure>
                </div>
                <div>
                  <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4F7F4]/50">
                    Scan with your phone
                  </p>
                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.velopaths.rider&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="btn-play-store"
                    aria-label="Get VeloPaths on Google Play"
                    className="inline-block transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <img
                      src="/images/google-play-badge.png"
                      alt="Get it on Google Play"
                      className="h-12 w-auto"
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/velopaths-rider/id6817941154"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="riders-app-store-badge"
                    aria-label="Download VeloPaths on the App Store"
                    className="inline-block transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <img
                      src="/images/app-store-badge.svg"
                      alt="Download on the App Store"
                      className="h-12 w-auto"
                    />
                  </a>
                </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rounded-2xl border border-[#F4F7F4]/15 bg-white/5 p-7 backdrop-blur sm:p-9">
                <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                  iPhone riders
                </p>
                <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight">
                  Now on iPhone too.
                </h3>
                <p className="mt-3 text-sm sm:text-base text-[#F4F7F4]/70">
                  The full VeloPaths experience is live on the App Store — the same curated
                  paths, the same vetted stops, the same hidden gems.
                </p>
                <a
                  href="https://apps.apple.com/us/app/velopaths-rider/id6817941154"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="btn-app-store-card"
                  aria-label="Download VeloPaths on the App Store"
                  className="mt-6 inline-block transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <img
                    src="/images/app-store-badge.svg"
                    alt="Download on the App Store"
                    className="h-12 w-auto"
                  />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTA
        overline="iOS + Android"
        title="Your next favorite stop is already on the map."
        sub="VeloPaths is live on the App Store and Google Play. Have a favorite local stop we should vet? Tell us about it."
        primary={{ label: "Get the app", to: "/riders#waitlist", testid: "btn-riders-waitlist" }}
        secondary={{ label: "Suggest a stop", to: "/contact", state: { tab: "general" }, testid: "btn-riders-suggest" }}
      />
    </main>
  );
}
